/**
 * The Unmatched demand list's state: status, country, industry and page live in the URL, so a link reopens the same
 * view; the industry text is debounced before it reaches the URL. Open is the default and is left out of the URL.
 */

import { useSearchParams } from 'react-router-dom';

import { useUnmatchedDemand } from '@/api/queries';
import type { DemandStatus, UnmatchedDemandParams } from '@/api/types';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';

import { DEMAND_STATUS_LABELS } from './demandUi';

const PAGE_SIZES = [25, 50, 100];

export function readDemandParams(search: URLSearchParams): UnmatchedDemandParams {
  const status = search.get('status');
  const page = Number(search.get('page'));
  const size = Number(search.get('size'));
  return {
    status: status && status in DEMAND_STATUS_LABELS ? (status as DemandStatus) : 'open',
    country: search.get('country') || null,
    industry: search.get('industry') ?? '',
    page: Number.isInteger(page) && page > 0 ? page : 1,
    page_size: PAGE_SIZES.includes(size) ? size : 25,
  };
}

export function writeDemandParams(params: UnmatchedDemandParams): URLSearchParams {
  const out = new URLSearchParams();
  if (params.status !== 'open') out.set('status', params.status);
  if (params.country) out.set('country', params.country);
  if (params.industry) out.set('industry', params.industry);
  if (params.page > 1) out.set('page', String(params.page));
  if (params.page_size !== 25) out.set('size', String(params.page_size));
  return out;
}

export function useDemandList() {
  const [search, setSearch] = useSearchParams();
  const params = readDemandParams(search);
  const update = (patch: Partial<UnmatchedDemandParams>) =>
    setSearch(writeDemandParams({ ...params, page: 1, ...patch }), { replace: true });
  const [text, setText] = useDebouncedSearch(params.industry, ({ q }) => update({ industry: q }));
  const list = useUnmatchedDemand(params);
  return { params, update, text, setText, list };
}
